import { useEffect, useState, useMemo } from "react";
import { PhoneVerify } from "@/components/PhoneVerify";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { Bell, Phone, Mail, MessageSquare, CheckCircle2, ShieldCheck, Lock, Crown, ShieldAlert, PhoneCall, Moon, ChevronDown, Info } from "lucide-react";
import { V } from "@/lib/vocab";

// Categories collapse. This list keeps growing (field services alone added four), so the screen
// opens as a scannable set of headings instead of a wall of switches. Choice is remembered.
const CLOSED_KEY = "askredline.notifcats.closed";
const readClosed = () => {
  try { return new Set(JSON.parse(localStorage.getItem(CLOSED_KEY) || "[]")); } catch { return new Set(); }
};
const writeClosed = (s) => {
  try { localStorage.setItem(CLOSED_KEY, JSON.stringify([...s])); } catch { /* ignore */ }
};

export function CollapsibleCategory({ cat, rows, testid, children }) {
  const [closed, setClosed] = useState(() => readClosed().has(cat));
  const on = rows.filter((e) => (e.value || {}).email || (e.value || {}).sms || (e.value || {}).call).length;
  const toggle = () => {
    const next = !closed;
    setClosed(next);
    const s = readClosed();
    next ? s.add(cat) : s.delete(cat);
    writeClosed(s);
  };
  return (
    <section data-testid={testid}>
      <button type="button" data-testid={`${testid}-toggle`} onClick={toggle} aria-expanded={!closed}
        className="group flex w-full items-center gap-2 border-b-2 border-ink/10 pb-1.5 text-left">
        <ChevronDown className={`h-4 w-4 shrink-0 text-ink/40 transition-transform duration-200 group-hover:text-ink/70 ${closed ? "-rotate-90" : ""}`} strokeWidth={2.5} />
        <h3 className="font-heading text-sm font-bold text-ink">{cat}</h3>
        <span className="ml-auto text-[11px] font-semibold text-ink/40">
          {closed ? `${on} of ${rows.length} on` : ""}
        </span>
      </button>
      {!closed && <div>{children}</div>}
    </section>
  );
}

// One row = one notification event. Channel toggles (email + text, plus an optional phone Call
// for events that support it, e.g. emergencies) sit inline so users pick each independently.
function EventRow({ entry, verified, onToggle, reportFrequency, communityFreq, onFrequency, alertOrder, onAlertOrder }) {
  const locked = { email: entry.lock_email, sms: entry.lock_sms };
  const v = entry.value || { email: false, sms: false };
  const isReport = entry.key === "community_report";
  const subscribed = !!v.email;
  const CADENCE = [["default", `Follow ${V.lc("org")}${communityFreq && communityFreq !== "off" ? ` (${communityFreq})` : ""}`], ["daily", "Daily"], ["weekly", "Weekly"], ["biweekly", "Biweekly"], ["monthly", "Monthly"], ["quarterly", "Quarterly"]];
  return (
    <div className="flex flex-col gap-2.5 sm:flex-row sm:items-start sm:gap-3 py-3 border-b border-sand-100 last:border-b-0" data-testid={`notif-row-${entry.key}`}>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-ink flex items-center gap-1.5">
          {entry.label}
          {entry.owner_only && (
            <span className="inline-flex items-center gap-0.5 rounded-full bg-amber-50 text-amber-800 border border-amber-200 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5">
              <Crown className="h-2.5 w-2.5" strokeWidth={2.5} /> Owner
            </span>
          )}
        </div>
        <div className="text-xs text-muted-foreground mt-0.5 leading-snug">{entry.description}</div>
        {isReport && onFrequency && (
          <label className={`mt-2 flex flex-wrap items-center gap-2 text-xs ${subscribed ? "" : "opacity-50"}`}>
            <span className="text-ink/50 font-semibold whitespace-nowrap">How often you get your copy:</span>
            <select data-testid="notif-report-frequency" value={reportFrequency || "default"} disabled={!subscribed}
              onChange={(e) => onFrequency(e.target.value)}
              className="rounded-lg border border-sand-200 bg-white px-2 py-1 text-xs font-medium outline-none focus:ring-2 focus:ring-redline-500/30 disabled:opacity-60">
              {CADENCE.map(([val, lab]) => <option key={val} value={val}>{lab}</option>)}
            </select>
          </label>
        )}
        {entry.key === "emergency_alerts" && onAlertOrder && (
          <label className={`mt-2 flex flex-wrap items-center gap-2 text-xs ${verified && v.call ? "" : "opacity-50"}`}>
            <span className="text-ink/50 font-semibold whitespace-nowrap">How to reach you first:</span>
            <select data-testid="notif-emergency-alert-order" value={alertOrder || "default"} disabled={!verified || !v.call}
              onChange={(e) => onAlertOrder(e.target.value)}
              className="rounded-lg border border-sand-200 bg-white px-2 py-1 text-xs font-medium outline-none focus:ring-2 focus:ring-redline-500/30 disabled:opacity-60">
              <option value="default">Community default (set on the ladder)</option>
              <option value="call_then_text">Call me first, text the details ~30 s later</option>
              <option value="text_then_call">Text me first, call only if I don't reply ACK</option>
              <option value="together">Call and text me at the same moment</option>
            </select>
            {(!verified || !v.call) && <span className="text-ink/45">Turn the call on to choose an order.</span>}
          </label>
        )}
      </div>
      <div className="flex items-center gap-3 shrink-0 pt-0.5 [&>div]:flex-col [&>div]:gap-1 sm:[&>div]:flex-row sm:[&>div]:gap-0 sm:[&_.ch]:hidden">
        <div className="w-11 flex justify-center" title={locked.email ? "Always on" : "Email"}>
          <span className="ch text-[9px] uppercase tracking-wider font-bold text-ink/40">Email</span>
          {locked.email ? (
            <span className="inline-flex items-center gap-0.5 rounded-md bg-emerald-50 text-emerald-700 text-[10px] font-bold px-1.5 py-0.5"><Lock className="h-2.5 w-2.5" /> On</span>
          ) : (
            <Switch data-testid={`notif-${entry.key}-email`}
              checked={!!v.email}
              onCheckedChange={(nv) => onToggle(entry.key, "email", nv)} />
          )}
        </div>
        <div className={`w-11 flex justify-center ${!verified && !locked.sms && !entry.email_only ? "opacity-40" : ""}`}
          title={entry.email_only ? "Email only" : verified ? "Text" : "Verify a mobile number to enable text alerts"}>
          <span className="ch text-[9px] uppercase tracking-wider font-bold text-ink/40">Text</span>
          {entry.email_only ? (
            <span className="text-ink/20 text-xs">—</span>
          ) : locked.sms ? (
            <span className="inline-flex items-center gap-0.5 rounded-md bg-emerald-50 text-emerald-700 text-[10px] font-bold px-1.5 py-0.5"><Lock className="h-2.5 w-2.5" /> On</span>
          ) : (
            <Switch data-testid={`notif-${entry.key}-sms`}
              checked={!!v.sms} disabled={!verified || !!locked.sms}
              onCheckedChange={(nv) => onToggle(entry.key, "sms", nv)} />
          )}
        </div>
        <div className={`w-11 flex justify-center ${entry.has_call && !verified ? "opacity-40" : ""}`}
          title={entry.has_call ? (verified ? "Phone call" : "Verify a mobile number to enable calls") : "Not available for this alert"}>
          <span className="ch text-[9px] uppercase tracking-wider font-bold text-ink/40">Call</span>
          {entry.has_call ? (
            <Switch data-testid={`notif-${entry.key}-call`}
              checked={!!v.call} disabled={!verified}
              onCheckedChange={(nv) => onToggle(entry.key, "call", nv)} />
          ) : (
            <span className="text-ink/20 text-xs">—</span>
          )}
        </div>
      </div>
    </div>
  );
}

// iter197c — accept optional `targetUserId`. When set, the component GETs/PUTs the
// super-admin endpoints against ANOTHER user's prefs instead of the caller's own. Phone
// verification is hidden in that mode (super admin can't verify someone else's phone).
export const NotificationPrefs = ({ onboarding = false, onDone, targetUserId = null, hideDone = false, hideContact = false }) => {
  const asAdmin = Boolean(targetUserId);
  // Filter the catalog to the community being looked at (super/portfolio roles have no home tenant).
  let activeTenant = "";
  try { activeTenant = localStorage.getItem("askredline.activeTenant") || ""; } catch {}
  const getUrl = asAdmin ? `/admin/users/${targetUserId}/notification-prefs` : `/me/notification-prefs${activeTenant ? `?tenant_id=${encodeURIComponent(activeTenant)}` : ""}`;
  const putUrl = asAdmin ? `/admin/users/${targetUserId}/notification-prefs` : "/me/notification-prefs";
  const [prefs, setPrefs] = useState(null);

  useEffect(() => {
    api.get(getUrl)
      .then((r) => setPrefs(r.data))
      .catch(() => setPrefs({ role: "hoa_manager", is_owner: false, categories: {}, phone: "", phone_verified: false, email: "" }));
  }, [getUrl]);

  const toggleEvent = async (key, channel, value) => {
    // Optimistic update: flip the row instantly so switching feels snappy.
    setPrefs((p) => {
      if (!p) return p;
      const next = { ...p, categories: { ...p.categories } };
      Object.keys(next.categories).forEach((cat) => {
        next.categories[cat] = next.categories[cat].map((e) =>
          e.key === key ? { ...e, value: { ...e.value, [channel]: value } } : e);
      });
      return next;
    });
    try {
      const r = await api.put(putUrl, { events: { [key]: { [channel]: value } } });
      setPrefs(r.data);
      toast.success(`Saved · ${channel === "email" ? "Email" : channel === "sms" ? "Text" : "Call"} ${value ? "on" : "off"}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
      // Re-fetch to snap back to server truth.
      api.get(getUrl).then((r) => setPrefs(r.data)).catch(() => {});
    }
  };

  const setFrequency = async (val) => {
    const prev = prefs?.report_frequency;
    setPrefs((p) => (p ? { ...p, report_frequency: val } : p));
    try {
      const r = await api.put(putUrl, { report_frequency: val });
      setPrefs(r.data);
      toast.success("Report cadence updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
      setPrefs((p) => (p ? { ...p, report_frequency: prev } : p));
    }
  };

  const setAlertOrder = async (val) => {
    const prev = prefs?.emergency_alert_order;
    setPrefs((p) => (p ? { ...p, emergency_alert_order: val } : p));
    try {
      const r = await api.put(putUrl, { emergency_alert_order: val });
      setPrefs(r.data);
      toast.success("Emergency alert order updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
      setPrefs((p) => (p ? { ...p, emergency_alert_order: prev } : p));
    }
  };

  const finish = () => onDone?.();

  const categories = useMemo(() => Object.entries(prefs?.categories || {}), [prefs]);
  const quiet = prefs?.quiet_hours || { enabled: false, start: "21:00", end: "07:00" };

  const saveQuiet = async (patch) => {
    const next = { ...quiet, ...patch };
    setPrefs((p) => (p ? { ...p, quiet_hours: next } : p));
    try {
      const r = await api.put(putUrl, { quiet_hours: next });
      setPrefs(r.data);
      toast.success(next.enabled ? `Quiet hours on · ${next.start}–${next.end}` : "Quiet hours off");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save quiet hours");
      api.get(getUrl).then((r) => setPrefs(r.data)).catch(() => {});
    }
  };

  if (!prefs) return <div className="py-6 flex justify-center"><div className="h-5 w-5 rounded-full border-2 border-redline-500 border-t-transparent animate-spin" /></div>;

  const verified = prefs.phone && prefs.phone_verified;

  return (
    <div data-testid="notif-prefs" className="space-y-5">
      {asAdmin && (
        <div data-testid="notif-admin-banner" className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 flex items-start gap-2.5">
          <ShieldAlert className="h-4 w-4 text-amber-700 shrink-0 mt-0.5" strokeWidth={2} />
          <div className="text-xs text-amber-900 leading-snug">
            <div className="font-semibold">Editing on behalf of {prefs.target_name || prefs.email || "this user"}</div>
            <div className="mt-0.5">Every change is written to their account and logged in the audit trail — they’ll see it on their next sign-in. This tool is for staff notification prefs only; {V.lcs("person")} manage theirs in the portal.</div>
          </div>
        </div>
      )}

      <p className="text-sm text-muted-foreground">
        {onboarding
          ? "Pick how you want to hear about each kind of activity. You can change this anytime from your profile menu. The in-app bell is always on for anything you care about."
          : asAdmin
            ? "Choose which alerts this team member should receive via email or text. Their in-app bell stays on for anything with either channel enabled."
            : `These are your personal alerts — how Ask Redline reaches you about activity in your ${V.lc("org")}. Turn each one on for email or text; the in-app bell stays on for anything with either channel enabled.`}
      </p>

      {/* iter294 — the #1 confusion we're fixing: people looked for "their notifications" on the
          Reports → "Schedule & email" screen, which is actually the SHARED community/board report.
          Name the difference and link them straight there so the two stop being conflated. */}
      {!asAdmin && !onboarding && (
        <div data-testid="notif-vs-report-note" className="rounded-xl border border-sky-100 bg-sky-50/70 px-4 py-3 flex items-start gap-2.5">
          <Info className="h-4 w-4 text-sky-600 shrink-0 mt-0.5" strokeWidth={2} />
          <div className="text-xs text-sky-900 leading-snug">
            <span className="font-semibold">Looking for the report that emails your whole {V.grp()} automatically?</span>{" "}
            That's a shared, {V.lc("org")}-wide schedule — set it under{" "}
            <Link to="/dashboard/reports" data-testid="notif-to-report-schedule" className="font-semibold underline underline-offset-2 hover:text-sky-700">Reports → Schedule &amp; email</Link>.
            This page controls only <span className="font-semibold">your own</span> alerts.
          </div>
        </div>
      )}

      {/* Contact header + mobile verification. Account settings shows its own "Contact & verification"
          card, so it passes hideContact; the admin view (Team → Manage) is read-only here. */}
      {!hideContact && (asAdmin ? (
        <div className="rounded-2xl border border-sand-200 bg-sand-50/60 px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3 text-sm">
          <div className="flex items-center gap-2 min-w-0 flex-1"><Mail className="h-4 w-4 text-ink/50 shrink-0" /><span className="text-ink/60">Email</span><span className="font-semibold text-ink truncate" data-testid="notif-header-email">{prefs.email || "no email on file"}</span></div>
          <div className="flex items-center gap-2 min-w-0 flex-1"><Phone className="h-4 w-4 text-ink/50 shrink-0" /><span className="text-ink/60">Text</span>
            {verified ? <><span className="font-semibold text-ink truncate" data-testid="notif-header-phone">{prefs.phone}</span><span className="inline-flex items-center gap-0.5 rounded-full bg-emerald-50 text-emerald-700 text-[10px] font-bold px-2 py-0.5"><CheckCircle2 className="h-2.5 w-2.5" strokeWidth={3} /> Verified</span></>
              : <span className="text-ink/50 italic">This user hasn't verified a mobile number yet — text toggles are locked until they do.</span>}
          </div>
        </div>
      ) : (
        <PhoneVerify prefs={prefs} onChanged={() => api.get(getUrl).then((r) => setPrefs(r.data)).catch(() => {})} />
      ))}

      {/* Quiet hours — iter256: the backend now honours this for every non-urgent text. */}
      <div className="rounded-2xl border border-sand-200 bg-white px-4 py-3.5" data-testid="notif-quiet-hours">
        <div className="flex items-start gap-3">
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-ink flex items-center gap-1.5">
              <Moon className="h-3.5 w-3.5 text-ink/50" strokeWidth={2} /> Quiet hours
            </div>
            <div className="text-xs text-muted-foreground mt-0.5 leading-snug">
              Hold non-urgent <strong>texts</strong> during these hours ({V.lc("org")}-local time). Emergencies,
              safety alerts and phone-line outages always come through.
            </div>
          </div>
          <Switch data-testid="notif-quiet-enabled" checked={!!quiet.enabled}
            onCheckedChange={(nv) => saveQuiet({ enabled: nv })} />
        </div>
        {quiet.enabled && (
          <div className="flex items-center gap-2 mt-3 text-xs">
            <span className="text-ink/50 font-semibold">From</span>
            <input type="time" data-testid="notif-quiet-start" value={quiet.start || "21:00"}
              onChange={(e) => saveQuiet({ start: e.target.value })}
              className="rounded-lg border border-sand-200 px-2 py-1 font-medium outline-none focus:ring-2 focus:ring-redline-500/30" />
            <span className="text-ink/50 font-semibold">to</span>
            <input type="time" data-testid="notif-quiet-end" value={quiet.end || "07:00"}
              onChange={(e) => saveQuiet({ end: e.target.value })}
              className="rounded-lg border border-sand-200 px-2 py-1 font-medium outline-none focus:ring-2 focus:ring-redline-500/30" />
          </div>
        )}
      </div>

      {/* Column headers */}
      <div className="hidden sm:flex items-center gap-3 pt-1">
        <div className="flex-1 text-[10px] uppercase tracking-[0.2em] font-bold text-ink/40">Notify me about</div>
        <div className="flex items-center gap-3 shrink-0">
          <span className="w-11 text-[10px] uppercase tracking-[0.15em] font-bold text-ink/40 text-center flex items-center gap-1 justify-center"><Mail className="h-3 w-3" /> Email</span>
          <span className="w-11 text-[10px] uppercase tracking-[0.15em] font-bold text-ink/40 text-center flex items-center gap-1 justify-center"><MessageSquare className="h-3 w-3" /> Text</span>
          <span className="w-11 text-[10px] uppercase tracking-[0.15em] font-bold text-ink/40 text-center flex items-center gap-1 justify-center"><PhoneCall className="h-3 w-3" /> Call</span>
        </div>
      </div>

      {categories.map(([cat, rows]) => (
        <CollapsibleCategory key={cat} cat={cat} rows={rows}
          testid={`notif-cat-${cat.toLowerCase().replace(/\s+/g, "-")}`}>
          {rows.map((entry) => (
            <EventRow key={entry.key} entry={entry} verified={verified} onToggle={toggleEvent}
              reportFrequency={prefs.report_frequency} communityFreq={prefs.report_community_frequency} onFrequency={setFrequency}
              alertOrder={prefs.emergency_alert_order} onAlertOrder={setAlertOrder} />
          ))}
        </CollapsibleCategory>
      ))}

      <div className="rounded-xl border border-emerald-100 bg-emerald-50/50 px-4 py-3 text-xs text-emerald-900 flex items-center gap-2">
        <Bell className="h-3.5 w-3.5 shrink-0" strokeWidth={2} />
        <span>Verification codes, magic links, and the in-app bell are always on — critical safety comms can&rsquo;t be turned off.</span>
      </div>

      {!hideDone && (
        <div className="pt-2">
          <button data-testid="notif-prefs-done" onClick={finish}
            className="w-full rounded-full bg-ink text-white font-semibold px-5 py-2.5 text-sm hover:bg-ink-700 transition-colors">
            {onboarding ? "Save & continue" : asAdmin ? "Close" : "Done"}
          </button>
        </div>
      )}
    </div>
  );
};
